import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IS_EMBEDDED } from '@lassi/config';
import {
  BREAKPOINTS,
  DrawZoomInMode,
  DrawZoomOutMode,
  MapMeasurementService,
  MapModes,
  MapService,
  MeasureType,
  SearchResultService,
} from '@lassi/webapp-shared';
import { LEAFLET_CONTROL_DIRECTIVE } from './leaflet-control.directive';

@Component({
  selector: 'lassi-side-toolbar',
  imports: [
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatButtonModule,
    MatDivider,
    MatTooltipModule,
  ],
  template: `
    @if (isWeb() || isEmbedded) {
      <button
        class="side-toolbar-button"
        matTooltip="Pan around the map"
        mat-icon-button
        [class.isActive]="
          mapService.mapMode() === MapModes.PAN &&
          !zoomMenuTrigger.menuOpen &&
          !measureMenuTrigger.menuOpen &&
          !anotherToolActive()
        "
        (click)="panMode()"
      >
        <mat-icon> pan_tool</mat-icon>
      </button>
      <button
        class="side-toolbar-button"
        matTooltip="Select an area to zoom in"
        mat-icon-button
        [class.isActive]="
          mapService.mapMode() === MapModes.DRAW_ZOOM_IN &&
          !zoomMenuTrigger.menuOpen &&
          !measureMenuTrigger.menuOpen &&
          !anotherToolActive()
        "
        (click)="zoomMode('zoomin')"
        [disabled]="isMeasuring()"
      >
        <span aria-hidden="true" class="hidden">drag_zoom_in</span>
        <mat-icon svgIcon="drag_zoom_in" />
      </button>
      <button
        class="side-toolbar-button"
        matTooltip="Select an area to zoom out"
        mat-icon-button
        [class.isActive]="
          mapService.mapMode() === MapModes.DRAW_ZOOM_OUT &&
          !zoomMenuTrigger.menuOpen &&
          !measureMenuTrigger.menuOpen &&
          !anotherToolActive()
        "
        (click)="zoomMode('zoomout')"
        [disabled]="isMeasuring()"
      >
        <span aria-hidden="true" class="hidden">drag_zoom_out</span>
        <mat-icon svgIcon="drag_zoom_out" />
      </button>
      <mat-divider />
    }
    <button
      matTooltip="Measure distance/area"
      class="side-toolbar-button menuButton"
      mat-icon-button
      [class.isActive]="
        (measureMenuTrigger.menuOpen || isMeasuring()) && !anotherToolActive()
      "
      (click)="measureMenuTrigger.openMenu()"
      [disabled]="isInteractingWithMap()"
      data-testid="measure-tools-button"
    >
      <mat-icon>straighten</mat-icon>
      <div
        #measureMenuTrigger="matMenuTrigger"
        [matMenuTriggerFor]="measureMenu"
      ></div>
    </button>
    <mat-menu #measureMenu [overlapTrigger]="false" class="side-toolbar-menu">
      <div class="measure-disclaimer">
        <mat-icon> info</mat-icon>
        <p class="mat-label-medium">Measurements are approximate only</p>
      </div>
      <button
        mat-menu-item
        [disabled]="isInteractingWithMap()"
        (click)="handleMeasureDistance()"
        data-testid="measure-distance-button"
      >
        <mat-icon>square_foot</mat-icon>
        Measure distance
      </button>
      <button
        mat-menu-item
        [disabled]="isInteractingWithMap()"
        (click)="handleMeasureArea()"
        data-testid="measure-area-button"
      >
        <mat-icon>aspect_ratio</mat-icon>
        Measure area
      </button>
    </mat-menu>
    <mat-divider />
    <button
      matTooltip="Zoom to"
      class="side-toolbar-button menuButton"
      [class.isActive]="zoomMenuTrigger.menuOpen && !anotherToolActive()"
      mat-icon-button
      (click)="zoomMenuTrigger.openMenu()"
    >
      <mat-icon>frame_inspect</mat-icon>
      <div
        #zoomMenuTrigger="matMenuTrigger"
        [matMenuTriggerFor]="zoomMenu"
      ></div>
    </button>
    <mat-menu #zoomMenu [overlapTrigger]="false" class="side-toolbar-menu">
      <button mat-menu-item (click)="mapService.goToMetro()">
        <mat-icon>apartment</mat-icon>
        Metro
      </button>
      <button mat-menu-item (click)="mapService.goToVictoria()">
        <span aria-hidden="true" class="hidden">victoria</span>
        <mat-icon svgIcon="victoria" />
        Victoria
      </button>
    </mat-menu>
    <mat-divider />
    <a
      matTooltip="Open in Google Maps"
      mat-icon-button
      [href]="googleMapsUrl()"
      target="_blank"
      class="google-maps"
    >
      <span aria-hidden="true" class="hidden">google_maps</span>
      <mat-icon svgIcon="google_maps" />
    </a>
    @if (canDismissSearchResults() && searchResultService.hasSearchResults()) {
      <mat-divider />
      <button
        matTooltip="Clear search results"
        class="side-toolbar-button menuButton"
        mat-icon-button
        (click)="searchResultService.clearSearchResults()"
      >
        <mat-icon>ink_eraser</mat-icon>
      </button>
    }

    <ng-content />
  `,
  styles: `
    @use '@angular/material' as mat;
    @use 'theme' as theme;
    @use 'strong-focus-indicator' as *;

    .measure-disclaimer {
      padding-left: var(--mat-menu-item-with-icon-leading-spacing);
      padding-right: var(--mat-menu-item-with-icon-trailing-spacing);

      > mat-icon {
        margin-right: var(--mat-menu-item-spacing);
        color: $focus-color;
        flex-shrink: 0;
      }

      color: mat.get-theme-color(theme.$light-theme, on-primary);
      display: flex;
      align-items: center;
    }

    :host {
      --icon-color: #{mat.get-theme-color(theme.$light-theme, on-primary)};
      --side-menu-background: #{mat.get-theme-color(
          theme.$light-theme,
          neutral,
          30
        )};
      display: flex;
      flex-direction: column;
      background: var(--side-menu-background);
      border-radius: 16px;
      box-shadow: var(--mat-app-elevation-shadow-level-3);
      padding: 0.5rem;
      gap: 0.25rem;

      .google-maps {
        color: var(--icon-color);
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [LEAFLET_CONTROL_DIRECTIVE],
})
export class SideToolbarComponent {
  readonly searchResultService = inject(SearchResultService);
  readonly mapService = inject(MapService);

  readonly isInteractingWithMap = input(false);
  readonly anotherToolActive = input(false);
  readonly canDismissSearchResults = input(true);

  private readonly menuTrigger =
    viewChild.required<MatMenuTrigger>('measureMenuTrigger');

  private readonly mapMeasurementService = inject(MapMeasurementService);
  isMeasuring = this.mapMeasurementService.isDrawing;

  isWeb = inject(BREAKPOINTS).isWeb;
  isEmbedded = inject(IS_EMBEDDED);

  protected readonly googleMapsUrl = this.mapService.googleMapsUrl;

  panMode() {
    this.mapService.resetMapMode();
  }

  zoomMode(type: 'zoomin' | 'zoomout') {
    if (type === 'zoomin') {
      this.mapService.switchMapMode(DrawZoomInMode, {
        mode: 'zoomin',
      });
    } else {
      this.mapService.switchMapMode(DrawZoomOutMode, {
        mode: 'zoomout',
      });
    }
  }

  handleMeasureDistance() {
    this.handleMeasureAction(() =>
      this.mapMeasurementService.startMeasure(MeasureType.DISTANCE),
    );
  }

  handleMeasureArea() {
    this.handleMeasureAction(() =>
      this.mapMeasurementService.startMeasure(MeasureType.AREA),
    );
  }

  private handleMeasureAction(action: () => void) {
    this.menuTrigger().closeMenu();
    this.mapMeasurementService.clearDrawing();
    this.mapMeasurementService.abortDrawing();
    action();
  }

  protected readonly MapModes = MapModes;
}
